/**
 * Tenant-portal API client. All authed calls carry the tenant JWT as a Bearer
 * token; the worker's aud:'tenant' allow-list + scope keep everything narrowed
 * to the caller's own tenant.
 */
import { createApiClient, resolveApiBase } from '@parkify/api-client'

/** The API origin (@parkify/api-client): VITE_API_BASE, else api.parkify.com. */
export const API_BASE = resolveApiBase({ VITE_API_BASE: import.meta.env.VITE_API_BASE as string | undefined })

// The worker returns `{ error: "…" }` on failures (utils/cors err()); the client
// surfaces that message so callers can show it as-is, and falls back to the raw
// body text, then `HTTP <status>`, as this module always has. An authed call
// that 401s means the 8h tenant session expired (or was revoked) mid-use —
// common on an always-signed-in kiosk — so the app is told to drop to sign-in
// rather than leave the screen on a dead "Couldn't load". A public call (no
// token) that 401s is a wrong link or code, not an expired session.
const api = createApiClient({
  baseUrl: API_BASE,
  errorMessage: ({ status, text }) => text || `HTTP ${status}`,
  onUnauthorized: (_res, ctx) => {
    if (ctx.authenticated && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('parkify:session-expired'))
    }
  },
})
export const CONSUMER_URL = (import.meta.env.VITE_CONSUMER_URL as string | undefined)
  ?? 'https://pay.parkify.com'

// freemax (slice 3a): free minutes with a cents cap — value = minutes, capCents = cap.
// until: free through an absolute local clock time ("park free until midnight")
// — value is unused; untilTime carries the 24h "HH:MM".
export type ValidationType = 'full' | 'flat' | 'percent' | 'minutes' | 'freemax' | 'until'
export type InventoryReset = 'calendar_month' | 'rolling_30d'
export type DeliveryMethod = 'coupon' | 'qr' | 'evalidation'
export type ValidationDayHours = { open: string; close: string }
/** 0 = Sunday … 6 = Saturday; absent/null day means unavailable that day. */
export type OperatingHours = { [day: number]: ValidationDayHours | null | undefined }
export type ValidationFunding = 'included' | 'chargeback' | 'prepaid'
export type ValidationSpendLimit =
  | { kind: 'amount'; cents: number }
  | { kind: 'uncapped'; confirmedAt: string }

/**
 * A program's COMMERCIAL availability, from the server (TEN-09). Mirrors
 * `ValidationCommercialAvailability` in packages/api-types
 * `validation-availability.ts`, which has the full contract.
 *
 * `status` is the redemption gate's own verdict on the spend limit or prepaid
 * block. `spend` and `block` arrive only for readers holding
 * `validations.reporting`; a Validation-only user gets the verdict and the
 * warning tier and never a figure.
 *
 * ⚠️ The block is NOT the inventory. `remaining` counts redemptions this
 * window; `block.used` counts grants ISSUED and not returned. Never add or
 * compare them.
 *
 * ⚠️ UNKNOWN IS NOT ZERO. `status: 'unknown'` (the ledger did not answer) and
 * an absent `commercial` (an older API, a paired tablet, a zone-pinned reader)
 * are both unknown, and unknown stays issuable: the server re-checks on
 * redemption.
 */
export interface ValidationCommercialAvailability {
  bound: 'none' | 'spend_limit' | 'prepaid_block'
  status: 'open' | 'reached' | 'unknown'
  /** While open. Spend limit: 'info' at 75%, 'urgent' at 90%. Block: 'low' at 20% or less left. */
  warning?: 'info' | 'urgent' | 'low'
  spend?: { spentCents: number; limitCents: number; periodStart: string; periodEnd: string }
  block?: { size: number; used: number; remaining: number; awaitingRedemption?: number; returned?: number }
}

export interface Validation {
  id?: string
  /** Stable validation-program identity; unlike `code`, it survives rotation. */
  validationId?: string
  code: string
  name?: string
  type: ValidationType
  value: number
  inventory?: { cap: number; reset: InventoryReset }
  capCents?: number
  untilTime?: string          // 'until' only — local clock time free parking ends, "HH:MM"
  /** Recurring local-time redemption window; absent means any day/time. */
  activeHours?: OperatingHours
  /**
   * Server's answer to "is this program inside `activeHours` right now", read
   * in the LOT's clock (zone -> client -> env timezone, the same resolution
   * redemption enforces). Never recompute it from the browser clock: a manager
   * outside the lot's timezone would read the window hours off.
   */
  availableNow?: boolean
  /** IANA timezone `activeHours` is read in; present only when there is a window. Labels only. */
  timezone?: string
  /** Uses per vehicle per `perPlateLimitPeriod`; 0/undefined = unlimited. */
  perPlateDailyLimit?: number
  /** Absent = 'day'. Weeks start Monday, months on the 1st, midnight in the lot's clock. */
  perPlateLimitPeriod?: 'day' | 'week' | 'month'
  /** Sessions allowed on this code AT ONCE; 0/undefined = unlimited. */
  maxConcurrent?: number
  deliveryMethod?: DeliveryMethod
  /** Operator-owned program funding. Absent means funding is not configured. */
  funding?: ValidationFunding
  /** Chargeback program's operator-owned monthly bound. */
  spendLimit?: ValidationSpendLimit
  /** Prepaid program's purchased block size, counted in issued grants. */
  prepaidLimit?: number
  zones: string[]
  active: boolean
  expiresAt?: string
  /** Usage INVENTORY left this window (redemptions). Not the prepaid block: see `commercial`. */
  remaining?: number
  /** Spend limit or prepaid block availability, from the server. Absent is unknown. */
  commercial?: ValidationCommercialAvailability
  /** Sessions using this code right now — computed by GET when capped. */
  activeNow?: number
  /**
   * Consumer-checkout URL with this code PRE-APPLIED, from the server.
   *
   * ⚠️ Never build this client-side. It is the same string the invite SMS
   * texts (`validationArrivalUrl`, api/src/validations/invite.ts), and the
   * scanned and texted destinations must not diverge — a QR that drops the
   * `code` param starts a session and applies nothing, silently.
   *
   * ABSENT when the validation names no zone ("applies to all"): no zone means
   * no link that pre-applies anything, so there is no QR to show. Render the
   * text-a-link path alone rather than a QR to the wrong place.
   */
  arrivalUrl?: string
  /**
   * The business's OWN printed "validate me" artwork, uploaded by Parkify in
   * the ops Validation QRs panel. A `data:` URI; absent means there is none and
   * the generated code is the real one.
   *
   * DISPLAY-ONLY, and only where the tenant is looking at their own signage —
   * see `OfferQrActions`. It is NOT verified to encode a code-bearing link, so
   * it must never be substituted into a QR handed to a specific visitor as the
   * thing that will apply their validation (see `InviteBlock` in Validate.tsx,
   * which deliberately keeps generating).
   *
   * Read-only here: uploading is Parkify-internal (edit.qr on the server).
   */
  customQrUrl?: string
  businessName?: string
  updatedAt?: string
  /**
   * Codes that USED to be this one, newest first — computed by GET, never sent
   * back on a write.
   *
   * The tenant is the party holding printed signage, and only Parkify knows when
   * an old code stops working, so this is the one piece of rotation state the
   * portal genuinely has to state out loud. A window whose `overlapUntil` has
   * already passed is still listed on purpose: "that code stopped working on the
   * 3rd" is the answer to "why are guests complaining".
   */
  retiredCodes?: Array<{ code: string; overlapUntil: string }>
  /**
   * The recurring schedule this code changes on, when it has one. Absent means
   * it never changes on its own — a permanent, valid configuration, not a
   * missing setting.
   *
   * `retiredCodes` above is the BACKWARD half of the same question (which
   * strings used to be this one). This is the FORWARD half, and it's the half a
   * tenant needs BEFORE spending money: `retiredCodes` can only tell them about
   * a rotation that has already happened, which is too late to affect what they
   * print. Read-only here — the schedule is set by Parkify in the ops console,
   * because only Parkify knows when the printed string dies.
   */
  rotation?: { cadenceDays: number; nextAt: string; overlapDays?: number }
}

/** Mirrors `RegistrationEmailKind` in the API's tenant_registrations.ts. */
export type RegistrationEmailKind =
  | 'request_registered' | 'request_waiting' | 'request_lapsed'
  | 'request_approved' | 'request_declined'

export interface ZoneRef {
  id: string
  displayName: string
  /** This lot accepts validation-backed parking only; no paid checkout exists. */
  validationOnly?: true
}
export interface TenantInfo {
  id: string
  name: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  /** Resolved name only; the selected colleague's pointer email stays private. */
  designatedContactName?: string
  /** Rotation-notice override; account contact and contact-on-file are fallbacks. */
  rotationEmail?: string
  zoneIds: string[]
  logoUrl?: string
  brandColor?: string
  /** How the tenant's monthly parking is paid for. Unset until the operator sets it. */
  permitModel?: 'included' | 'purchased' | 'overage'
}
export type TenantAccessLevel = 'full' | 'operational' | 'validation'
export type TenantCapability =
  | 'parkers.view' | 'parkers.manage' | 'parkers.export'
  | 'permits.view' | 'permits.manage'
  | 'validations.view' | 'validations.issue' | 'validations.request' | 'validations.reporting'
  | 'billing.view' | 'billing.pay'
  | 'users.view' | 'users.manage'
export interface MeResponse {
  tenant: TenantInfo
  zones: ZoneRef[]
  access: { level: TenantAccessLevel; capabilities: TenantCapability[] }
  /** Who the portal renders for. `viewAs` only under a staff view-as lens. */
  viewer?: { email: string | null; name?: string; viewAs?: { operatorEmail: string; readOnly: boolean; mode?: 'full' } }
}

export const tenantCan = (me: MeResponse, capability: TenantCapability): boolean =>
  // Rolling-deploy compatibility: the old API response has no `access` block.
  // Treat that short window exactly like the pre-feature portal (Full). Once
  // the API is live, its canonical capability list drives presentation and its
  // independent route gate remains the real security boundary either way.
  me.access?.capabilities?.includes(capability) ?? true

export interface Redemption {
  code: string
  plate: string
  zoneId: string
  zoneName: string
  /** Taken off at checkout. Alone, it under-reports any session that was extended. */
  discountCents: number
  /** Taken off by this session's extensions under the same grant. 0 on
   *  pre-0027 rows and on any API old enough not to send it. */
  extensionDiscountCents?: number
  /** discount + extensions — what the validation actually cost. Optional for the
   *  same reason; fall back to discountCents. */
  totalDiscountCents?: number
  finalCents: number
  redeemedAt: string
  /** Issuing tablet's device id, or null when no tablet was involved. */
  deviceId?: string | null
  /**
   * The issuing tablet's CURRENT name, resolved server-side (migration 0031).
   * Null means the driver applied the code themselves at checkout — a coupon or
   * a QR — which is a real outcome, not a missing value, so it must not render
   * as a blank. Absent (undefined) on an API old enough not to send the field.
   */
  deviceName?: string | null
}

export const READ_ONLY_MESSAGE = 'Read-only while viewing as a tenant user.'

/** Reads the token's own `readOnly` claim. A UI hint only: the server enforces it. */
function tokenIsReadOnly(token: string): boolean {
  try {
    const body = token.split('.')[1]
    if (!body) return false
    return (JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as { readOnly?: unknown }).readOnly === true
  } catch { return false }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Screens report a failed write with a native alert() (17 call sites). Under
 * a read-only lens every write "fails" with READ_ONLY_MESSAGE, and the toast
 * already says so, so the alert is a second, BLOCKING copy of it: found live
 * 2026-09-25, a Save froze the tab until the dialog was dismissed. Drops only
 * that exact message; every other alert is untouched. Returns the uninstaller.
 */
export function suppressReadOnlyAlerts(win: Pick<Window, 'alert'> = window): () => void {
  const original = win.alert
  win.alert = (message?: unknown) => {
    if (message === READ_ONLY_MESSAGE) return
    original.call(win, message)
  }
  return () => { win.alert = original }
}

async function call<T>(path: string, init: RequestInit, token?: string): Promise<T> {
  // A view-as lens renders the target's buttons; a write from one never leaves
  // the browser. The app shows a toast off this event. The server refuses the
  // same write with a viewas.blocked row if this is ever bypassed.
  if (token && !SAFE_METHODS.has((init.method ?? 'GET').toUpperCase()) && tokenIsReadOnly(token)) {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('parkify:read-only'))
    throw new Error(READ_ONLY_MESSAGE)
  }
  // The status rides along on the thrown ApiError so the kiosk can tell "the
  // server could not answer" (502/503/504) from an ordinary refusal; `code` and
  // `body` let a screen explain a refusal the server classified. Message-only
  // callers are unaffected: it is still an Error with the same message.
  const { body, headers, ...rest } = init
  return api.request<T>(path, {
    ...rest,
    bearer: token,
    ...(body ? { body, headers: { ...(headers as Record<string, string>), 'Content-Type': 'application/json' } } : { headers }),
  })
}

// ── Auth (public) ──
export const requestTenantLink = (email: string) =>
  call<{ ok: boolean }>('/api/portal/tenant/request-link', { method: 'POST', body: JSON.stringify({ email }) })

export const verifyTenantLink = (token: string) =>
  call<{ token: string; email: string; tenantId: string; viewAs?: import('./session').ViewAsInfo }>('/api/portal/tenant/verify', { method: 'POST', body: JSON.stringify({ token }) })

export type StaffTenant = { id: string; name: string; active?: boolean }

/**
 * Parkify staff use the same Google exchange as Patrol and the console. The
 * returned ops token is only a bridge to the existing audited sign-in-as flow;
 * it is never accepted by tenant routes or stored as a tenant session.
 */
export async function signInTenantStaffWithGoogle(accessToken: string): Promise<{ token: string; email: string }> {
  const auth = await call<{ token: string; email: string; clientId?: string | null }>(
    '/api/auth/google',
    { method: 'POST', body: JSON.stringify({ accessToken, rememberMe: false }) },
  )
  if ((auth.clientId ?? null) !== null || !auth.email.toLowerCase().endsWith('@parkify.com')) {
    throw new Error('Parkify staff sign-in requires a @parkify.com account.')
  }
  return { token: auth.token, email: auth.email }
}

export const listStaffTenants = (staffToken: string) =>
  call<StaffTenant[]>('/api/tenants', { method: 'GET' }, staffToken)

/** Reuse the same single-use, audited tenant lens that the ops console opens. */
export async function openStaffTenant(staffToken: string, tenantId: string) {
  const { url } = await call<{ url: string }>(
    `/api/tenants/${encodeURIComponent(tenantId)}/sign-in-as`,
    { method: 'POST' },
    staffToken,
  )
  const token = new URL(url).searchParams.get('token')
  if (!token) throw new Error('The tenant sign-in link was incomplete.')
  return verifyTenantLink(token)
}

// ── Authed (tenant JWT) ──
export const getMe = (token: string) =>
  call<MeResponse>('/api/tenants/me', { method: 'GET' }, token)
export type TenantScope = { id: string; name: string }
export const listTenantScopes = (token: string) => call<{ tenants: TenantScope[] }>('/api/tenants/me/scopes', { method: 'GET' }, token)
export const switchTenantScope = (token: string, tenantId: string) => call<{ token: string; tenantId: string }>('/api/tenants/me/switch', { method: 'POST', body: JSON.stringify({ tenantId }) }, token)

export interface ResidentRecord {
  unit: string
  phoneLast4: string[]
  residents: ResidentContact[]
  plates: string[]
  guestPin: string
  weeklyGuestHours: number
  source: 'csv' | 'pdf'
  importedAt: string
}
export interface ResidentContact {
  id: string
  firstName: string
  lastName: string
  phoneLast4: string
  email?: string
}
export interface ResidentContactInput {
  firstName: string
  lastName: string
  phone?: string
  email?: string
}
export const listResidentRecords = (token: string) =>
  call<{ records: ResidentRecord[] }>('/api/tenants/me/resident-records', { method: 'GET' }, token)
export const importResidentCsv = (token: string, csv: string) =>
  call<{ ok: boolean; imported: number }>('/api/tenants/me/resident-records/import', { method: 'POST', body: JSON.stringify({ csv }) }, token)
export const createResidentContact = (token: string, unit: string, resident: ResidentContactInput) =>
  call<{ ok: boolean; record: ResidentRecord }>(`/api/tenants/me/resident-records/${encodeURIComponent(unit)}/residents`, { method: 'POST', body: JSON.stringify(resident) }, token)
export const updateResidentContact = (token: string, unit: string, residentId: string, resident: ResidentContactInput) =>
  call<{ ok: boolean; record: ResidentRecord }>(`/api/tenants/me/resident-records/${encodeURIComponent(unit)}/residents/${encodeURIComponent(residentId)}`, { method: 'PATCH', body: JSON.stringify(resident) }, token)
export const deleteResidentContact = (token: string, unit: string, residentId: string) =>
  call<{ ok: boolean; record: ResidentRecord }>(`/api/tenants/me/resident-records/${encodeURIComponent(unit)}/residents/${encodeURIComponent(residentId)}`, { method: 'DELETE' }, token)
export interface ResidentImportDocument {
  id: string; filename: string; contentType: string; sizeBytes: number; status: 'queued'; uploadedAt: string
}
export const listResidentImportDocuments = (token: string) =>
  call<{ documents: ResidentImportDocument[] }>('/api/tenants/me/resident-import-documents', { method: 'GET' }, token)
export const storeResidentPdf = (token: string, file: Pick<ResidentImportDocument, 'filename' | 'contentType' | 'sizeBytes'> & { dataBase64: string }) =>
  call<{ ok: boolean; document: ResidentImportDocument }>('/api/tenants/me/resident-import-documents', { method: 'POST', body: JSON.stringify(file) }, token)
export type ResidentAllowanceMode = 'calendar_week' | 'rolling_7_days'
export const getResidentConfig = (token: string) => call<{ enabled: boolean; allowanceMode: ResidentAllowanceMode; defaultGuestZoneId?: string }>('/api/tenants/me/resident-config', { method: 'GET' }, token)
export const saveResidentConfig = (token: string, allowanceMode: ResidentAllowanceMode, defaultGuestZoneId?: string) => call<{ enabled: boolean; allowanceMode: ResidentAllowanceMode; defaultGuestZoneId?: string }>('/api/tenants/me/resident-config', { method: 'PUT', body: JSON.stringify({ allowanceMode, ...(defaultGuestZoneId ? { defaultGuestZoneId } : {}) }) }, token)

export interface PortalUser {
  email: string
  name?: string
  active: boolean
  accessLevel?: TenantAccessLevel
  invitedAt: string
  lastLoginAt?: string
  /** Present on the Full roster row selected as the tenant account contact. */
  isDesignatedContact?: boolean
}

export const listPortalUsers = (token: string) =>
  call<PortalUser[]>('/api/tenants/me/users', { method: 'GET' }, token)

export const savePortalUser = (
  token: string,
  body: { email: string; name?: string; accessLevel: TenantAccessLevel },
) => call<PortalUser>('/api/tenants/me/users', {
  method: 'POST', body: JSON.stringify(body),
}, token)

export const removePortalUser = (token: string, email: string) =>
  call<{ ok: boolean }>(`/api/tenants/me/users?email=${encodeURIComponent(email)}`, { method: 'DELETE' }, token)

export const setDesignatedContact = (token: string, email: string | null) =>
  call<{ ok: boolean; designatedContactEmail: string | null; designatedContactName?: string }>(
    '/api/tenants/me/users',
    { method: 'PATCH', body: JSON.stringify({ email }) },
    token,
  )

export const listValidations = (token: string) =>
  call<Validation[]>('/api/validations', { method: 'GET' }, token)

export interface ValidationRequest {
  id: string
  tenantId: string
  clientId: string | null
  requestedByEmail?: string
  requestedByName?: string
  message?: string
  /** The program asked about (its stable id). Absent on the unscoped ask, and on older requests. */
  programId?: string
  programName?: string
  status: 'pending' | 'acknowledged' | 'fulfilled' | 'declined'
  requestedAt: string
  updatedAt: string
}

export const listValidationRequests = (token: string) =>
  call<{ requests: ValidationRequest[] }>('/api/validations/requests', { method: 'GET' }, token)

/**
 * Ask the operator for more. `programId` names the program (TEN-08); leave it
 * out for the unscoped ask that names none. The server checks the program is
 * this tenant's own and answers 404 otherwise.
 */
export const requestMoreValidations = (token: string, opts: { programId?: string; message?: string } = {}) =>
  call<{ request: ValidationRequest }>('/api/validations/requests', {
    method: 'POST',
    body: JSON.stringify({ message: opts.message?.trim() || undefined, programId: opts.programId || undefined }),
  }, token)

/**
 * The id a request names a program by: the same stable identity the server
 * stores (`validationId`, or the code on a program saved before it had one).
 */
export const requestProgramKey = (o: Pick<Validation, 'validationId' | 'id' | 'code'>): string =>
  o.validationId ?? o.id ?? o.code

/**
 * A manager-facing PREVIEW of what a time-based validation is worth, right
 * now and at a representative evening hour — never a redemption. Both
 * `null` for a discount type that already states an exact value
 * (full/flat/percent), or when the zone can't be metered at that instant.
 */
export interface ValidationEstimate { nowCents: number | null; eveningCents: number | null }
export const estimateValidation = (token: string, code: string) =>
  call<ValidationEstimate>(`/api/validations/${encodeURIComponent(code)}/estimate`, { method: 'GET' }, token)

// No saveValidation / deleteValidation here on purpose. A tenant never writes
// a validation program; the operator does, from the console, and the API
// refuses a tenant token on POST/DELETE /api/validations.

/** First-page totals for the whole range. Absent on later pages and from an
 *  API old enough not to send them. */
export interface RedemptionHistory {
  redemptions: Redemption[]
  total?: number
  /** Counts per UTC hour, `YYYY-MM-DDTHH`. */
  hourly?: { hour: string; count: number }[]
}

export const listHistory = (token: string, code?: string, range?: { from: string; to: string }, before?: string) => {
  const q = new URLSearchParams()
  if (code) q.set('code', code)
  if (range) { q.set('from', range.from); q.set('to', range.to) }
  if (before) q.set('before', before)
  return call<RedemptionHistory>(`/api/validations/history${q.toString() ? `?${q}` : ''}`, { method: 'GET' }, token)
}

// ── API keys (PR9 — POS / partner integrations) ──
// A long-lived machine credential a point-of-sale backend uses to call the
// public /v1 redeem endpoint. The plaintext token is returned ONCE by create
// and is unrecoverable after — the UI must surface it immediately.
export interface ApiKey {
  id: string
  name: string
  prefix: string
  last4: string
  scopes: string[]
  createdAt: string
  createdBy: string
  lastUsedAt?: string
}
export const listApiKeys = (token: string) =>
  call<{ keys: ApiKey[] }>('/api/tenants/me/api-keys', { method: 'GET' }, token)

export const createApiKey = (token: string, name: string) =>
  call<{ key: ApiKey; token: string }>('/api/tenants/me/api-keys', { method: 'POST', body: JSON.stringify({ name }) }, token)

export const revokeApiKey = (token: string, id: string) =>
  call<{ ok: boolean }>(`/api/tenants/me/api-keys/${encodeURIComponent(id)}`, { method: 'DELETE' }, token)

// ── Staff self-registration ──
// The public half takes NO token: this is the link a tenant's staff receive,
// and a sign-in in front of it defeats the point. Submitting grants nothing —
// it queues a `pending` row that only a manager can turn into a permit.
export interface RegisterInfo {
  tenantId: string
  name: string
  logoUrl?: string
  brandColor?: string
  /** Present when the link named a shared program that allocates seats to this tenant. */
  programId?: string
  programName?: string
  /** The link asked for a reserved bay AND this tenant's allocation can honour it. */
  reservedBay?: boolean
  /** Present when the link named one of the tenant's own named permits (`&permit=`). */
  permitId?: string
  permitName?: string
  /** Plates one person may register (the location's setting). Absent if unread or a program link; the submit still checks. */
  platesPerParker?: number
}
export const getRegisterInfo = (tenantId: string, programId?: string, reserved = false, permitId?: string) =>
  call<RegisterInfo>(`/api/portal/tenant/register-info?t=${encodeURIComponent(tenantId)}${programId ? `&program=${encodeURIComponent(programId)}` : ''}${programId && reserved ? '&reserved=1' : ''}${!programId && permitId ? `&permit=${encodeURIComponent(permitId)}` : ''}`, { method: 'GET' })

export interface ResidentGuestInfo { tenantId: string; name: string; logoUrl?: string; brandColor?: string; defaultGuestZoneId?: string; weeklyGuestHours: number }
export const getResidentGuestInfo = (tenantId: string) =>
  call<ResidentGuestInfo>(`/api/portal/tenant/resident-guest-info?t=${encodeURIComponent(tenantId)}`, { method: 'GET' })
export interface ResidentGuestRecord { unit: string; plates: string[]; guestPin: string; weeklyGuestHours: number; phoneLast4: string[] }
export const startPhoneOtp = (phone: string, turnstileToken?: string) =>
  call<{ ok: boolean }>('/api/portal/auth/phone/start', {
    method: 'POST',
    body: JSON.stringify({ phone, ...(turnstileToken ? { turnstileToken } : {}) }),
  })
export const checkPhoneOtp = (phone: string, code: string) =>
  call<{ token: string }>('/api/portal/auth/phone/check', { method: 'POST', body: JSON.stringify({ phone, code }) })
export const lookupResidentGuest = (token: string, tenantId: string) =>
  call<ResidentGuestRecord>('/api/portal/tenant/resident-guest/lookup', { method: 'POST', body: JSON.stringify({ tenantId }) }, token)
export interface ResidentAllowance { mode: ResidentAllowanceMode; usedMinutes: number; remainingMinutes: number; activeMinutes: number; periodStart: string; periodEnd: string }
export const getResidentAllowance = (token: string, tenantId: string) =>
  call<ResidentAllowance>('/api/portal/tenant/resident-guest/allowance', { method: 'POST', body: JSON.stringify({ tenantId }) }, token)
export interface ResidentGuestStartResult {
  ok: boolean; txnId: string; shortCode?: string; zoneId: string; plate: string; unit: string
  minutesStarted: number; requestedMinutes: number; clamped: boolean; expiresAt: string
}
export const startResidentGuestSession = (token: string, input: { tenantId: string; plate: string; durationMinutes: number; requestId: string }) =>
  call<ResidentGuestStartResult>('/api/portal/tenant/resident-guest/start', { method: 'POST', body: JSON.stringify(input) }, token)
export interface ResidentGuestCancelResult { ok: boolean; alreadyCanceled?: boolean; txnId: string; restoredMinutes: number; canceledAt: string }
export const cancelResidentGuestSession = (token: string, input: { tenantId: string; txnId: string }) =>
  call<ResidentGuestCancelResult>('/api/portal/tenant/resident-guest/cancel', { method: 'POST', body: JSON.stringify(input) }, token)

export const submitRegistration = (body: {
  tenantId: string; firstName: string; lastName: string; plate: string; plates?: string[]; issuingState?: string; plateStates?: PlateStateInput[]; phone: string; email?: string
  /**
   * From the sign-up LINK (`&type=`), never a field the applicant chooses — the
   * link is what tags the request, so approval is "confirm this person works
   * here" rather than "pick a permit". An unknown or missing value falls back
   * server-side rather than 400ing: these links get forwarded and truncated in
   * the wild, and a lost query param must not dead-end a staff member.
   */
  permitType?: string
  /** Also from the link (`&program=`): the shared program this request lands in. The server refuses an unknown one. */
  programId?: string
  /** Also from the link (`&permit=`): the named permit this request lands on. The server refuses one that is not the tenant's. */
  permitId?: string
}) =>
  call<{ ok: boolean; duplicate: boolean; blocked: boolean; status: RegistrationStatus; message: string }>(
    '/api/portal/tenant/register', { method: 'POST', body: JSON.stringify(body) },
  )

// ── Shared programs ──
// Fixed allocations the operator granted this tenant inside shared programs.
// Only THIS tenant's seats and counts come back; approval of a program request
// reserves one of these seats server-side, so the card is display only.
/** One reserved bay in this tenant's pool; `issuanceId` names the seat holding it, null when free. */
export interface ProgramPoolBay { bayId: string; label: string; section: string | null; zoneId: string; state: 'active' | 'out_of_service'; issuanceId: string | null }
export interface ProgramAllocation {
  id: string; name: string; zoneIds: string[]; seats: number; paused: boolean; held: number; issued: number
  /** This tenant's OWN reserved-bay pool. Optional on the wire for a portal bundle ahead of the Worker. */
  bayPool?: ProgramPoolBay[]
  /** Days of notice before a removed parker's seat is released; 0/absent = released on the spot. */
  releaseNoticeDays?: number
}
export const listProgramAllocations = (token: string) =>
  call<{ programs: ProgramAllocation[] }>('/api/tenants/me/program-allocations', { method: 'GET' }, token)

export type RegistrationStatus = 'pending' | 'approved' | 'declined' | 'lapsed'
export interface TenantRegistration {
  /** Named permits: the permit the sign-up link named (`&permit=`). */
  targetPermitId?: string
  /** Shared program the sign-up link named; approval takes one seat from this tenant's allocation and has no end date. */
  programId?: string
  programName?: string
  /** The applicant used a reserved-bay sign-up link; the approver should pick a bay. */
  reservedBay?: boolean
  id: string
  firstName: string
  lastName: string
  plate: string
  /** Every plate on the request. Absent on single-plate requests, where
   *  `plate` is the whole story. */
  plates?: string[]
  issuingState?: string
  /** Per-plate jurisdiction. Absent on requests submitted before the form
   *  asked for it — which is exactly when the approver has to supply one. */
  plateStates?: PlateStateInput[]
  phone?: string
  email?: string
  status: RegistrationStatus
  createdAt: string
  decidedAt?: string
  decidedBy?: string
  lapsedAt?: string
  events?: { id: string; kind: 'request_registered' | 'request_approved' | 'request_declined' | 'request_lapsed'; at: string; actorEmail?: string; contact?: string }[]
  /**
   * Notices Parkify actually SENT for this request — appended only after the
   * provider accepted, so this is a delivery record, not an intent log.
   *
   * ⚠️ Keep in step with `RegistrationEmailKind` on the API. This union was
   * missing `request_approved`/`request_declined` for as long as those emails
   * existed, which is precisely why nothing caught the label for them falling
   * through to a catch-all that claimed the request had lapsed.
   */
  emailEvents?: { id: string; kind: RegistrationEmailKind; at: string; recipient: string; waitingDays?: number; lapsesIn?: number }[]
  permitId?: string
  /**
   * Which permit type this request came in for, from the link used. Absent on
   * anything registered before per-type links existed — treat as the default
   * (staff) bucket, which is what the server counts it against.
   */
  permitType?: string
}
/**
 * How the tenant's staff spaces are paid for, and how the spaces they hold
 * RIGHT NOW split into free and billable.
 *
 *   included    every space is bundled into the lease; nothing is ever billable
 *   purchased   the tenant buys every space
 *   overage     `includedPermitCount` come with the lease, the rest are billed
 *
 * ⚠️ COUNTS, NOT PRICES. The server deliberately attaches no rate to any of
 * this (see api/src/tenants/permit_billing.ts) — real rates are per-zone rate
 * products, and a number invented to make this screen look finished would be a
 * fabricated figure sitting in front of a billing decision. So the UI may say
 * "this one is billable"; it must never say what it costs.
 */
export interface TenantPermitBilling {
  model: 'included' | 'purchased' | 'overage'
  includedPermitCount: number
  /** Of the spaces held RIGHT NOW, how many cost the tenant nothing. */
  included: number
  /** Of the spaces held RIGHT NOW, how many the tenant is billed for. */
  billable: number
  /**
   * What ONE MORE space would do — server-derived, because it is an off-by-one
   * away from the split above and the wrong answer is a surprise charge (12
   * included + 12 held ⇒ `billable: 0`, yet the next approval IS billed).
   * Never re-derive this in the client.
   */
  next: {
    billable: boolean
    /** 1-based ordinal of the space the next approval would grant. */
    nth: number
    includedPermitCount: number
  }
}

/**
 * `seats.cap` is null when the operator has set no cap. `seats.used` is null
 * when the count could not be read — that is UNKNOWN, not zero, and the UI
 * must not render it as "0 used" against a cap.
 *
 * `billing` is ABSENT when the count was unavailable or the operator hasn't
 * set a permit model. Absent means say nothing about money — there is no safe
 * default (guessing "included" hides a real charge; guessing "purchased"
 * invents one).
 */
export const listRegistrations = (token: string, status?: RegistrationStatus) =>
  call<{
    registrations: TenantRegistration[]
    seats: { cap: number | null; used: number | null }
    billing?: TenantPermitBilling
    /**
     * Per-type sub-caps and their usage. ABSENT when the operator configured
     * none — the UI then shows the single overall number, exactly as before.
     * Counted on permits only (a purchased seat has no permit type), which is
     * why it is a companion to `seats`, never a replacement for it.
     */
    seatTypes?: {
      caps: Record<string, number>
      usedByType: Record<string, number>
      /**
       * Per-type approval pressure — `pending` waiting for that bucket, `left`
       * still free under its sub-cap, and `short` = how many of the queue there
       * is NO room for. Derived server-side (`derivePendingPressure`) because a
       * test file in this package runs in no CI job.
       *
       * ⚠️ ABSENT is not "nothing waiting". The server omits it when the caller
       * filtered the list to a status that cannot prove the pending set, and a
       * type with no sub-cap never appears at all. Render nothing when it's
       * missing; never substitute 0.
       */
      pressure?: Record<string, { pending: number; left: number; short: number }>
    }
  }>(
    `/api/tenants/me/registrations${status ? `?status=${status}` : ''}`, { method: 'GET' }, token,
  )

/** `validUntil` is REQUIRED (YYYY-MM-DD) — the server rejects an open-ended grant. */
export const approveRegistration = (
  token: string,
  id: string,
  validUntil: string,
  /** Jurisdictions the approver filled in for plates the request never named
   *  one for. Omitted entirely when the request already carried them. */
  plateStates: PlateStateInput[] = [],
  /** A reserved bay from this tenant's program pool, for a program request only. */
  bayId?: string | null,
  /** Named permits: the permit to approve onto, when the request did not name one or the approver picks another. */
  permitId?: string | null,
) =>
  call<{ ok: boolean; registration: TenantRegistration }>(
    `/api/tenants/me/registrations/${encodeURIComponent(id)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({ validUntil, ...(plateStates.length ? { plateStates } : {}), ...(bayId ? { bayId } : {}), ...(permitId ? { permitId } : {}) }),
    }, token,
  )

export const declineRegistration = (token: string, id: string, reason?: string) =>
  call<{ ok: boolean; registration: TenantRegistration }>(
    `/api/tenants/me/registrations/${encodeURIComponent(id)}/decline`,
    { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) }, token,
  )

/**
 * Mint a permit directly, no self-registration request first — for a manager
 * who already knows who's parking. Same seat-cap enforcement as approving a
 * registration (the server runs both through one shared check); the only
 * difference is skipping the pending-request record.
 * `validUntil` is REQUIRED (YYYY-MM-DD) — the server rejects an open-ended grant.
 */
export const addParker = (
  token: string,
  parker: {
    firstName: string; lastName: string; plate?: string; plates?: string[]; issuingState?: string; plateStates?: PlateStateInput[]; permitType?: string; validUntil: string
    phone?: string; email?: string
    /** Named permits: the permit this parker goes on. */
    permitId?: string
  },
) => call<{ id: string }>('/api/tenants/me/parkers', { method: 'POST', body: JSON.stringify(parker) }, token)

/**
 * Email a colleague a link to the sign-up form.
 *
 * Grants nothing — it asks them to register, and a manager still approves. The
 * request carries ONLY the address (and optionally which sign-up form to open):
 * the email body is built entirely server-side and takes nothing from here,
 * which is what stops this being a way to send arbitrary mail on Parkify
 * letterhead. Don't add a message field.
 */
export const sendRegisterNudge = (
  token: string,
  invite: { email: string; permitType?: string; permitId?: string },
) => call<{ ok: boolean; message: string }>(
  '/api/tenants/me/registration-invites',
  { method: 'POST', body: JSON.stringify(invite) },
  token,
)

// ── Front-desk tablets ("stations") ──
// A device credential (`ptd_…`, the Worker's tenant_devices.ts) is what keeps a
// counter tablet signed in across shifts. It is strictly NARROWER than the
// `ptk_` API key above — the Worker's `isDeviceRouteAllowed` confines it to
// looking up and validating cars, and it cannot reach these device routes at
// all, so a tablet can neither mint another tablet nor revoke itself.
//
// These call `/api/tenants/me/devices`, which derives the tenant from the
// caller's own `aud:'tenant'` token — there is no tenantId parameter to get
// wrong, and no scope for this client to pass or forget.
export interface TenantDevice {
  id: string
  name: string
  prefix: string
  last4: string
  /** Current code carried by the one-time setup link; not the station authority. */
  defaultCode?: string
  /** Durable validation-program identity this station is authorized to issue. */
  programId?: string
  createdAt: string
  createdBy: string
  lastUsedAt?: string
}

export const listDevices = (token: string) =>
  call<{ devices: TenantDevice[] }>('/api/tenants/me/devices', { method: 'GET' }, token)

/** Returns the device AND its plaintext credential — the ONLY time it exists. */
export const createDevice = (token: string, body: { name: string; defaultCode?: string; programId?: string }) =>
  call<{ device: TenantDevice; token: string }>('/api/tenants/me/devices', { method: 'POST', body: JSON.stringify(body) }, token)

export const revokeDevice = (token: string, id: string) =>
  call<{ ok: boolean }>(`/api/tenants/me/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }, token)

/**
 * The URL a tablet is set up with. Built against this app's OWN origin rather
 * than a configured host, because the portal that mints the link is by
 * definition the portal the tablet should open — no env var to drift.
 * Mirrors `tabletLinkUrl` in the ops console's TabletLinkPanel.
 */
export function tabletLinkUrl(deviceToken: string, code?: string): string {
  const base = `${window.location.origin}/?d=${encodeURIComponent(deviceToken)}`
  return code ? `${base}&code=${encodeURIComponent(code)}` : base
}

// ── E-validation kiosk (PR6) ──
// The "tablet, no scan" flow: front desk enters a plate, the worker finds the
// driver's active paid session and refunds the discount to their card. Every
// non-error outcome comes back 200 with a `status` envelope; true failures
// (missing fields, ledger down, Stripe) throw via call()'s error extraction.
export type EvalidateStatus =
  | 'validated' | 'already_validated' | 'no_session' | 'already_free' | 'rejected' | 'inactive'
  | 'time_appended' | 'no_time_added' | 'fully_covered'
  // Slice 4 — phone lookup only: 2+ cars on one number → the server returns a
  // bare count (never a session list to a shared iPad); staff re-look-up by plate.
  | 'multiple_sessions'
  // Two-step kiosk (2026-08-27) — the LOOKUP-only pass found a refundable
  // session and moved no money. Only ever returned for `preview: true`.
  | 'found'
export interface EvalidateResult {
  status: EvalidateStatus
  message: string
  refundedCents?: number
  businessName?: string
  plate?: string
  // Slice 4 — kiosk session card + honest SMS beat:
  maskedPlate?: string   // phone lookups only ever learn the plate's tail
  zoneName?: string
  expiresAt?: string
  newExpiresAt?: string
  appendedMinutes?: number
  smsSent?: boolean      // true only when the driver confirmation text went out
  count?: number         // multiple_sessions only
  // TEN-02, `found` only: opaque reference to the session this preview showed.
  // The confirm sends it back as `expectedSession`, and the server refuses a
  // confirm whose current lookup resolves to any other session or program.
  sessionRef?: string
}
// Look up by plate (staff typed it) or phone (slice 4 — whichever the customer
// has on hand). Exactly one of the two; the worker applies the refund on a
// single match, so the UI must arm its confirm step BEFORE calling this.
// `preview: true` runs the lookup half ONLY — no refund, no ledger write. The
// kiosk always previews first, so the confirm row it arms describes a car that
// actually exists rather than promising a refund against a bare phone number.
// A confirm (no `preview`) must carry `expectedSession`: the `sessionRef` of
// the preview the person reviewed. Without it the server refuses the confirm.
export const evalidate = (
  token: string, code: string,
  lookup: { plate?: string; phone?: string },
  opts?: { preview?: boolean; expectedSession?: string },
) =>
  call<EvalidateResult>('/api/validations/evalidate', {
    method: 'POST',
    body: JSON.stringify({
      code, ...lookup,
      ...(opts?.preview ? { preview: true } : {}),
      ...(!opts?.preview && opts?.expectedSession ? { expectedSession: opts.expectedSession } : {}),
    }),
  }, token)

// Slice 4 — registration invite: no session to credit, so text the visitor a
// checkout link with this validation pre-applied. Rate-limited server-side
// (per-IP + per-number); an inactive/expired code comes back as a 200 status
// envelope like evalidate's.
export const sendInvite = (token: string, code: string, phone: string) =>
  call<{ ok?: boolean; status?: string; message: string }>('/api/validations/invite', { method: 'POST', body: JSON.stringify({ code, phone }) }, token)

// Slice-4 follow-up — staff-initiated FREE session start: the lookup found no
// session, the validation fully covers a window (full/minutes/freemax), so
// staff collect the plate and start the $0 session at the counter. The server
// sizes + prices the window itself and rejects anything not fully covered
// (flat/percent, a binding freemax cap) toward the invite path. Response is
// shared-iPad safe: masked plate + zone + expiry only.
export type KioskFreeStartStatus = 'started' | 'already_parked' | 'rejected' | 'inactive'
export interface KioskFreeStartResult {
  status: KioskFreeStartStatus
  message: string
  reason?: string          // rejected sub-kind: paid_discount | cap_binds | unpriceable_window | not_sellable
  maskedPlate?: string
  zoneName?: string
  expiresAt?: string
  durationLabel?: string
  businessName?: string
  smsSent?: boolean        // only when a phone was passed; reflects the actual send
}
// idempotencyKey: mint ONCE when the confirm step arms (crypto.randomUUID());
// a double-tap of the same confirm replays as the SAME session server-side.
export const startFreeSession = (
  token: string, code: string,
  body: { plate: string; phone?: string; idempotencyKey: string },
) =>
  call<KioskFreeStartResult>('/api/validations/free-session', { method: 'POST', body: JSON.stringify({ code, ...body }) }, token)

// ── Branding (tenant self-serve) ──
// PATCH the tenant's own record (brand color + contact). Returns the record.
export const updateBranding = (token: string, body: { brandColor?: string; name?: string; rotationEmail?: string }) =>
  call<TenantInfo>('/api/tenants/me', { method: 'PATCH', body: JSON.stringify(body) }, token)

// Upload raw logo bytes (NOT JSON — own fetch so Content-Type is the image mime).
export async function uploadLogo(token: string, blob: Blob): Promise<{ logoUrl: string; tenant: TenantInfo }> {
  return api.request<{ logoUrl: string; tenant: TenantInfo }>('/api/tenants/me/logo', {
    method: 'POST',
    bearer: token,
    headers: { 'Content-Type': blob.type || 'image/png' },
    body: blob,
  })
}

export const deleteLogo = (token: string) =>
  call<{ ok: boolean; tenant: TenantInfo }>('/api/tenants/me/logo', { method: 'DELETE' }, token)

// ── Permit roster ──
/**
 * One permit as the TENANT sees it — a projection, not the operator's record.
 * The server withholds price/notes/createdBy/linked-customer PII on purpose
 * (see api/src/tenants/permit_roster.ts); don't add them to this type expecting
 * them to arrive.
 */
export interface RosterVehicle {
  plate: string
  state?: string
  /** "State unknown" was chosen for this plate: it needs a state. */
  stateUnknown?: true
  driver?: string
  description?: string
  phone?: string
  email?: string
  /** Parker identity on a roster pool permit; plates sharing it are one person. */
  holderId?: string
  /** A paying tenant ended this parker inside the notice period: billed through this date (server-set). */
  noticeBilledThrough?: string
}
export interface PlateStateInput {
  plate: string
  state: string
  /** "State unknown" chosen on purpose (ruling 2026-10-21); `state` is '' then. Manager adds and edits only. */
  stateUnknown?: true
}
export interface TenantVehicleEdit extends PlateStateInput {
  originalPlate?: string
}
export interface RosterPermit {
  id: string
  label: string
  permitType?: string
  /** Present on a permit materialized from a shared-program seat; Remove then releases the seat via revokeProgramSeat. */
  programId?: string
  issuanceId?: string
  /** Reserved bays this permit holds (zone_bays ids); labels come from the tenant's program pool. */
  bayIds?: string[]
  /** Every plate on the permit. Driver/description only where it was filled in. */
  vehicles: RosterVehicle[]
  zoneIds: string[]
  /** Plates one parker may hold on this permit (the location's setting, set by the operator). Absent if it could not be read. */
  platesPerParker?: number
  validFrom: string
  /** Each plate's enforcement start day (lot timezone); missing plate → validFrom. */
  plateStartDates?: Record<string, string>
  /** `''` = no expiration. Render that, never a blank or a fake date. */
  validUntil: string
  status: 'active' | 'expiringSoon' | 'expired'
  active: boolean
  /**
   * The permit's weekly access schedule, pre-rendered by the server —
   * "Mon–Fri 7 AM–6:30 PM".
   *
   * ⚠️ ABSENT MEANS 24/7 and always will (`Permit.accessHours` back-compat).
   * Render that as a positive statement, never as a blank or an unknown.
   *
   * A STRING because docs/PERMIT-ACCESS-HOURS-SPEC.md §12 refuses giving a
   * tenant surface anything that could become an hours editor: nothing a
   * manager can reach writes this field, so they get a sentence to read and no
   * structure to bind an input to.
   */
  accessHours?: string
}

/**
 * ⚠️ A failed read 503s rather than answering `[]` — so a thrown error here
 * means "couldn't load", and an empty array genuinely means "no permits". Do
 * not collapse the two in the UI: a manager who reads "no permits" off a failed
 * call believes their staff just lost access.
 */
export const listTenantPermits = (token: string) =>
  call<{ permits: RosterPermit[]; permitsByName?: boolean; platesPerParker?: number }>('/api/tenants/me/permits', { method: 'GET' }, token)

export const updateTenantPermitVehicle = (
  token: string,
  permitId: string,
  plate: string,
  detail: Pick<RosterVehicle, 'driver' | 'description' | 'phone' | 'email' | 'state'> & {
    plates?: string[]
    /** Plates to remove, resolved server-side against the permit as it is now. */
    removePlates?: string[]
    originalPlates?: string[]
    vehicleEdits?: TenantVehicleEdit[]
    permitType?: string
    validUntil?: string
  },
) => call(`/api/tenants/me/permits/${encodeURIComponent(permitId)}/vehicles/${encodeURIComponent(plate)}`, {
  method: 'PATCH', body: JSON.stringify(detail),
}, token)

/** The server's answer to "what is this parker billed through if their end date becomes X", on the lot's clock. */
export type PaidNoticePreview =
  | { paysMonthly: false }
  | { paysMonthly: true; today: string; billedThrough: string | null; billedThroughIfEndedToday: string | null }

export const previewPaidNotice = (token: string, permitId: string, plate: string, validUntil: string) =>
  call<PaidNoticePreview>(
    `/api/tenants/me/permits/${encodeURIComponent(permitId)}/vehicles/${encodeURIComponent(plate)}/notice?validUntil=${encodeURIComponent(validUntil)}`,
    { method: 'GET' }, token)

/** A 409 that would end a paid parker's seat outright: the server's code, and its dates on the lot clock. */
export const PAID_REMOVAL_REFUSED_CODE = 'paid_removal_refused'

export const removeTenantPermit = (token: string, permitId: string) =>
  call<{ ok: boolean }>(`/api/tenants/me/permits/${encodeURIComponent(permitId)}`, { method: 'DELETE' }, token)
/**
 * Release one of this tenant's shared-program seats. The server releases the seat only after the permit delete is confirmed.
 * On a program with a release notice (Parkify-set) the seat is NOT released: `releaseNotice.endsOn` is the lot-local date
 * it ends, and the parker keeps access until then.
 */
export const revokeProgramSeat = (token: string, issuanceId: string) =>
  call<{ ok: boolean; issuance: { id: string; state: string }; releaseNotice?: { endsOn: string; days: number } }>(`/api/tenants/me/program-issuances/${encodeURIComponent(issuanceId)}/revoke`, { method: 'POST' }, token)

// ── Billing (read-only) ──
/**
 * What this tenant's validations gave away, by period.
 *
 * ⚠️ THIS IS NOT AN AMOUNT DUE, and the UI must never render it as one.
 * Validation funding is program-grain, so one tenant-wide period may combine
 * included, chargeback, and prepaid offers. The only honest aggregate claim is
 * "this is what the discounts were worth"; the Validation cards state each
 * offer's own funding terms. Same discipline as #1736 and permit_billing.ts:
 * say nothing about money you cannot attribute.
 *
 * ⚠️ COUNTS AND VALUES, NEVER A PERMIT PRICE. `omissions` is the server telling
 * you the total is partial BY DESIGN — staff parking spaces have no rate
 * anywhere yet, so they are absent from `lines` rather than present at zero.
 * Render the reason; do not "complete" the invoice with an estimate.
 */
export type TenantInvoiceStatus = 'draft' | 'issued' | 'paid' | 'void'

export interface TenantInvoiceLine {
  kind: 'validation_discounts'
  label: string
  /** Redemptions counted — not a multiplicand for some unit price. */
  quantity: number
  amountCents: number
}

export interface TenantInvoiceOmission {
  kind: 'permit_seats'
  /** Why it isn't here. Rendered as-is; it deliberately carries no figure. */
  reason: string
}

/**
 * The period accruing right now. Recomputed on every read and stored nowhere,
 * which is why it has no `id` and no `status` — there is no record to reference.
 */
export interface TenantAccrual {
  /** Inclusive calendar dates, read in `timezone`. */
  periodStart: string
  periodEnd: string
  /** The IANA clock the period boundaries were resolved in — the LOT's, not UTC. */
  timezone: string
  /** The half-open instant window actually summed over: [start, end). */
  periodStartAt: string
  periodEndAt: string
  lines: TenantInvoiceLine[]
  totalCents: number
  omissions: TenantInvoiceOmission[]
  generatedAt: string
}

/** A stored invoice: the same numbers, plus the identity an accrual doesn't have. */
export interface TenantInvoiceRecord extends TenantAccrual {
  id: string
  status: TenantInvoiceStatus
}

/** One priced line of the current staff-parking estimate: spaces × the operator's rate. */
export interface PermitStatementLine {
  zoneIds: string[]
  /** Lot names, resolved server-side; falls back to the raw id. */
  zoneNames: string[]
  permitType: string
  /** Tenant-facing label for the type ("Staff"), from the server. */
  typeLabel: string
  count: number
  rateCents: number
  subtotalCents: number
}

/**
 * Something the estimate cannot price, with a SPACE COUNT and never an amount.
 * Rendered verbatim — a tenant not told which spaces are missing from the total
 * will reasonably conclude those spaces are free.
 */
export interface PermitStatementOmission {
  kind: 'no_rate' | 'rate_varies_by_lot' | 'no_lot_recorded' | 'purchased_seats' | 'lease_allowance'
  reason: string
  spaces: number
}

/**
 * The current staff-parking estimate — spaces held × the rate the OPERATOR entered.
 *
 * ⚠️ REPORT-ONLY. NOT A STATEMENT, INVOICE OR BILL. No due date, no terms,
 * no balance, and no way to pay it here. The screen must name the current-roster
 * limitation and say that this report changes no invoice and creates no charge.
 *
 * Absent (the field is simply missing) when there is nothing honest to show — no
 * rates entered, an all-included lease, or an unknown billing model. Absent is
 * NOT zero: never render a missing estimate as "$0.00".
 *
 * The `PermitStatement` name is retained for source and wire compatibility with
 * the existing `permitStatement` response field. It is not a product label.
 */
export interface PermitStatement {
  /** The month the operator's MONTHLY rate applies to. */
  periodStart: string
  periodEnd: string
  timezone: string
  /**
   * When the spaces were counted. ⚠️ The counts are LIVE, not averaged over the
   * period. The screen says so rather than implying a period figure or a change
   * to an invoice.
   */
  countedAt: string
  lines: PermitStatementLine[]
  /** Sum of `lines` only. Partial wherever `omissions` says so. */
  subtotalCents: number
  /**
   * True when a lease allowance applies and has NOT been deducted — which spaces
   * it covers is the operator's call. The subtotal must be labelled accordingly.
   */
  beforeLeaseAllowance: boolean
  omissions: PermitStatementOmission[]
}

/**
 * ⚠️ A failed read 503s rather than answering `{ totalCents: 0 }` or `[]`, so a
 * thrown error here means "couldn't load". Do not collapse it into an empty
 * state: "$0.00 this month" off a dead call reads as a quiet month, and "no
 * invoices" reads as a history that vanished.
 *
 * `permitStatement` and `permitStatementUnavailable` are legacy wire names for
 * the current estimate. They are the two SEPARATE ways the estimate can be
 * missing, and the UI must not merge them: no rates
 * configured is a settled answer, a failed permit read is not, and a tenant whose
 * estimate silently disappeared would conclude their operator removed it.
 */
export const getTenantBilling = (token: string) =>
  call<{
    invoicedBy: 'parkify_bills' | 'operator_bills'
    invoicedByTier: 'location' | 'client' | 'platform'
    current: TenantAccrual
    invoices: TenantInvoiceRecord[]
    permitStatement?: PermitStatement
    permitStatementUnavailable?: boolean
  }>('/api/tenants/me/invoices', { method: 'GET' }, token)
